import React, { useState, useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

import { BasePage, Helmet, StaticContext } from '@sm/webassets';
import { t } from '@sm/intl';

import MRXNav from './MRXNav';
import { NAV_VARIANT } from '../enums';
import previewImage from './featured-image.png';

const COPY = {
  pageTitle: {
    id: 'mrx.MRXPage.pageTitle',
    defaultMessage: 'Market Research Solutions | SurveyMonkey',
    description: '[Type: Label][Vis: med] - Page title for all Market Research Experience pages',
  },
  pageDescription: {
    id: 'mrx.MRXPage.pageDescription',
    defaultMessage:
      'Collect quality data from your target market with expert solutions, ' +
      'global online panels, and market research services to help you get the insights you need, fast.',
    description: '[Type: Label][Vis: med] - Page meta description for all Market Research Experience pages',
  },
};

const MRXPage = ({ pageTitle, previewImageHref, noIndex, children, canonicalLink }) => {
  const {
    environment: { subdomain, domain, tld },
    req: { url },
  } = useContext(StaticContext);
  const requestUrl = `https://${subdomain}.${domain}.${tld}${url}`;
  const [mobileNavOpen, _setMobileNavOpen] = useState(false);
  const scrollPosition = useRef(0);
  const setMobileNavOpen = () => {
    if (window && !mobileNavOpen) scrollPosition.current = window.pageYOffset;
    _setMobileNavOpen(!mobileNavOpen);
  };
  useEffect(() => {
    if (mobileNavOpen) document.body.classList.add('sm-mrx-nav--no-scroll');
    else document.body.classList.remove('sm-mrx-nav--no-scroll');
  }, [mobileNavOpen]);
  // Have to manually append this to comply with Twitter's requirements
  const mPreviewImageHref = previewImageHref.startsWith('http') ? previewImageHref : `https:${previewImageHref}`;

  return (
    <BasePage
      color="light"
      includeHeader
      includeFooter={!mobileNavOpen}
      pageId={`Market Research - ${pageTitle}`}
      legacyWeb=""
      sticky={false}
      isMRX
    >
      <Helmet>
        <title>{t(COPY.pageTitle)}</title>
        <meta name="description" content={t(COPY.pageDescription)} />

        <meta property="og:description" content={t(COPY.pageDescription)} />
        <meta property="og:title" content={t(COPY.pageTitle)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={requestUrl} />
        <meta property="og:image" content={mPreviewImageHref} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="628" />

        {/* TWITTER META */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@SurveyMonkey" />
        <meta name="twitter:title" content={t(COPY.pageTitle)} />
        <meta name="twitter:description" content={t(COPY.pageDescription)} />
        <meta name="twitter:image" content={mPreviewImageHref} />

        <If condition={noIndex}>
          <meta name="robots" content="noindex" />
        </If>

        <If condition={canonicalLink}>
          <link data-rh="true" rel="canonical" href={canonicalLink} />
        </If>
      </Helmet>
      <MRXNav variant={NAV_VARIANT.LIGHT} mobileOpen={mobileNavOpen} setMobileOpen={setMobileNavOpen} />
      {children}
    </BasePage>
  );
};

MRXPage.propTypes = {
  pageTitle: PropTypes.string.isRequired,
  noIndex: PropTypes.bool,
  previewImageHref: PropTypes.string,
  children: PropTypes.node,
  canonicalLink: PropTypes.string,
};

MRXPage.defaultProps = {
  children: null,
  previewImageHref: previewImage,
  noIndex: false,
  canonicalLink: null,
};

export default MRXPage;
